import {
  Descriptions,
  Table,
  Button,
  Badge,
  Space,
  Typography,
  Spin,
  Modal,
  Form,
  InputNumber,
  DatePicker,
  Select,
  Input,
  message,
} from 'antd';
import { useState } from 'react';
import { ArrowLeftOutlined, DownloadOutlined, MailOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  useInvoiceById,
  useIssueInvoice,
  useCancelInvoice,
  useRecordPayment,
} from '@hooks/useFinance';
import type {
  PaymentDto,
  InvoiceStatus,
  PaymentMethod,
  RecordPaymentRequest,
} from '@/types/finance';
import { downloadInvoicePdf, emailInvoice } from '@/api/exportApi';

const MD_STATUS_COLOR_MAP: Record<InvoiceStatus, string> = {
  Draft: 'default',
  Issued: 'processing',
  PartiallyPaid: 'warning',
  Paid: 'success',
  Overdue: 'error',
  Cancelled: 'default',
};

const MD_STATUS_LABEL_MAP: Record<InvoiceStatus, string> = {
  Draft: 'Чернетка',
  Issued: 'Виставлено',
  PartiallyPaid: 'Частково сплачено',
  Paid: 'Сплачено',
  Overdue: 'Прострочено',
  Cancelled: 'Скасовано',
};

// Лише члени C# enum PaymentMethod — інше значення сервер відхилить
const MD_PAYMENT_METHOD_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: 'BankTransfer', label: 'Банківський переказ' },
  { value: 'Cash', label: 'Готівка' },
  { value: 'Card', label: 'Картка' },
];

const MD_PAYMENT_METHOD_LABEL_MAP: Record<PaymentMethod, string> = {
  BankTransfer: 'Банківський переказ',
  Cash: 'Готівка',
  Card: 'Картка',
};

interface InvoiceDetailPageProps {
  invoiceId: string;
  onBack: () => void;
}

export default function InvoiceDetailPage({
  invoiceId,
  onBack,
}: InvoiceDetailPageProps) {
  const [mdIsPaymentModalOpen, setMdIsPaymentModalOpen] = useState(false);
  const [mdIsCancelModalOpen, setMdIsCancelModalOpen] = useState(false);
  const [mdCancelReason, setMdCancelReason] = useState('');
  const [mdPdfLoading, setMdPdfLoading] = useState(false);
  const [mdEmailLoading, setMdEmailLoading] = useState(false);

  const [mdPaymentForm] = Form.useForm();

  const { data: mdInvoice, isLoading: mdIsLoading } = useInvoiceById(invoiceId);
  const mdIssueInvoice = useIssueInvoice();
  const mdCancelInvoice = useCancelInvoice();
  const mdRecordPayment = useRecordPayment();

  const mdHandleIssue = () => {
    mdIssueInvoice.mutate(invoiceId);
  };

  const mdHandleCancel = () => {
    mdCancelInvoice.mutate(
      { id: invoiceId, reason: mdCancelReason },
      {
        onSuccess: () => {
          setMdIsCancelModalOpen(false);
          setMdCancelReason('');
        },
      }
    );
  };

  const mdHandleRecordPayment = async () => {
    const mdValues = await mdPaymentForm.validateFields();
    const mdRequest: RecordPaymentRequest = {
      invoiceId,
      amount: mdValues.amount,
      paymentDate: mdValues.paymentDate.format('YYYY-MM-DD'),
      paymentMethod: mdValues.paymentMethod,
      reference: mdValues.reference,
      notes: mdValues.notes,
    };
    await mdRecordPayment.mutateAsync(mdRequest);
    setMdIsPaymentModalOpen(false);
    mdPaymentForm.resetFields();
  };

  const mdOpenPaymentModal = () => {
    mdPaymentForm.setFieldsValue({
      amount: mdInvoice?.balanceDue ?? 0,
      paymentDate: dayjs(),
      paymentMethod: 'BankTransfer',
    });
    setMdIsPaymentModalOpen(true);
  };

  const mdHandleDownloadPdf = async () => {
    setMdPdfLoading(true);
    try {
      await downloadInvoicePdf(invoiceId);
    } finally {
      setMdPdfLoading(false);
    }
  };

  const mdHandleEmailInvoice = async () => {
    setMdEmailLoading(true);
    try {
      await emailInvoice(invoiceId);
      message.success('Рахунок надіслано на email');
    } finally {
      setMdEmailLoading(false);
    }
  };

  const mdCanIssue = mdInvoice?.status === 'Draft';
  const mdCanPay =
    mdInvoice?.status === 'Issued' ||
    mdInvoice?.status === 'PartiallyPaid' ||
    mdInvoice?.status === 'Overdue';
  const mdCanCancel =
    mdInvoice?.status === 'Draft' || mdInvoice?.status === 'Issued';

  const mdPaymentColumns: ColumnsType<PaymentDto> = [
    {
      title: 'Дата',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      width: 120,
      render: (mdValue: string) => dayjs(mdValue).format('DD.MM.YYYY'),
    },
    {
      title: 'Сума',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      align: 'right',
      render: (mdValue: number) =>
        mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 }),
    },
    {
      title: 'Спосіб оплати',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 180,
      render: (mdValue: PaymentMethod) => MD_PAYMENT_METHOD_LABEL_MAP[mdValue] ?? mdValue,
    },
    {
      title: 'Номер документа',
      dataIndex: 'reference',
      key: 'reference',
      width: 200,
      render: (mdValue?: string | null) => mdValue || '—',
    },
    {
      title: 'Примітки',
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      render: (mdValue?: string | null) => mdValue || '—',
    },
  ];

  if (mdIsLoading || !mdInvoice) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
            Назад
          </Button>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Рахунок {mdInvoice.number}
          </Typography.Title>
          <Badge
            status={
              MD_STATUS_COLOR_MAP[mdInvoice.status] as
                | 'default'
                | 'processing'
                | 'success'
                | 'error'
                | 'warning'
            }
            text={MD_STATUS_LABEL_MAP[mdInvoice.status]}
          />
        </Space>
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={mdHandleDownloadPdf}
            loading={mdPdfLoading}
          >
            Завантажити PDF
          </Button>
          <Button
            icon={<MailOutlined />}
            onClick={mdHandleEmailInvoice}
            loading={mdEmailLoading}
          >
            Надіслати на email
          </Button>
        </Space>
      </div>

      <Descriptions bordered column={3} size="small" style={{ marginBottom: 24 }}>
        <Descriptions.Item label="Клієнт">{mdInvoice.customerName}</Descriptions.Item>
        <Descriptions.Item label="Дата виставлення">
          {dayjs(mdInvoice.issueDate).format('DD.MM.YYYY')}
        </Descriptions.Item>
        <Descriptions.Item label="Термін оплати">
          {dayjs(mdInvoice.dueDate).format('DD.MM.YYYY')}
        </Descriptions.Item>
        <Descriptions.Item label="Сума">
          {mdInvoice.totalAmount.toLocaleString('uk-UA', { minimumFractionDigits: 2 })}{' '}
          {mdInvoice.currency}
        </Descriptions.Item>
        <Descriptions.Item label="Сплачено">
          {mdInvoice.paidAmount.toLocaleString('uk-UA', { minimumFractionDigits: 2 })}{' '}
          {mdInvoice.currency}
        </Descriptions.Item>
        <Descriptions.Item label="Залишок">
          <Typography.Text type={mdInvoice.balanceDue > 0 ? 'danger' : 'success'}>
            {mdInvoice.balanceDue.toLocaleString('uk-UA', { minimumFractionDigits: 2 })}{' '}
            {mdInvoice.currency}
          </Typography.Text>
        </Descriptions.Item>
        {mdInvoice.salesOrderNumber && (
          <Descriptions.Item label="Замовлення">
            {mdInvoice.salesOrderNumber}
          </Descriptions.Item>
        )}
        {mdInvoice.notes && (
          <Descriptions.Item label="Примітки" span={3}>
            {mdInvoice.notes}
          </Descriptions.Item>
        )}
        {mdInvoice.cancelReason && (
          <Descriptions.Item label="Причина скасування" span={3}>
            <Typography.Text type="danger">{mdInvoice.cancelReason}</Typography.Text>
          </Descriptions.Item>
        )}
      </Descriptions>

      <Typography.Title level={5}>Історія оплат</Typography.Title>

      <Table<PaymentDto>
        columns={mdPaymentColumns}
        dataSource={mdInvoice.payments}
        rowKey="id"
        pagination={false}
        size="small"
        locale={{ emptyText: 'Оплати відсутні' }}
      />

      <div
        style={{
          padding: '16px 0',
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
          borderTop: '1px solid #f0f0f0',
          marginTop: 16,
        }}
      >
        {mdCanIssue && (
          <Button
            type="primary"
            onClick={mdHandleIssue}
            loading={mdIssueInvoice.isPending}
          >
            Виставити
          </Button>
        )}
        {mdCanPay && (
          <Button type="primary" onClick={mdOpenPaymentModal}>
            Записати оплату
          </Button>
        )}
        {mdCanCancel && (
          <Button danger onClick={() => setMdIsCancelModalOpen(true)}>
            Скасувати
          </Button>
        )}
      </div>

      {/* Payment Modal */}
      <Modal
        title="Записати оплату"
        open={mdIsPaymentModalOpen}
        onOk={mdHandleRecordPayment}
        onCancel={() => setMdIsPaymentModalOpen(false)}
        confirmLoading={mdRecordPayment.isPending}
        okText="Записати"
        cancelText="Скасувати"
      >
        <Form form={mdPaymentForm} layout="vertical">
          <Form.Item
            name="amount"
            label="Сума"
            rules={[{ required: true, message: 'Введіть суму' }]}
          >
            <InputNumber min={0.01} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="paymentDate"
            label="Дата оплати"
            rules={[{ required: true, message: 'Оберіть дату' }]}
          >
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="paymentMethod"
            label="Спосіб оплати"
            rules={[{ required: true, message: 'Оберіть спосіб' }]}
          >
            <Select options={MD_PAYMENT_METHOD_OPTIONS} />
          </Form.Item>
          <Form.Item name="reference" label="Номер документа">
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="notes" label="Примітки">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Cancel Modal */}
      <Modal
        title="Скасування рахунку"
        open={mdIsCancelModalOpen}
        onOk={mdHandleCancel}
        onCancel={() => setMdIsCancelModalOpen(false)}
        confirmLoading={mdCancelInvoice.isPending}
        okText="Скасувати рахунок"
        okButtonProps={{ danger: true }}
        cancelText="Назад"
      >
        <Typography.Paragraph>
          Вкажіть причину скасування рахунку:
        </Typography.Paragraph>
        <Input.TextArea
          rows={3}
          value={mdCancelReason}
          onChange={(mdEvent) => setMdCancelReason(mdEvent.target.value)}
          placeholder="Причина скасування..."
        />
      </Modal>
    </>
  );
}
